import type { Unsubscribe } from "../../types/unsubscribe";
import type { SubscribableWithState } from "../../subscribable/subscribable";
import type { ThreadListItemRuntimePath } from "./paths";
import type { ThreadListRuntimeCoreBinding } from "./thread-list-runtime";
import { notifyEventListeners } from "../../utils/notify-event-listeners";

export type ThreadListItemEventPayload = {
  /**
   * @deprecated State-derivable. Compare `s.threads.mainThreadId` against the
   * item's `s.threadListItem.id` via `useAuiState` instead. Kept for backward
   * compatibility.
   */
  switchedTo: Record<string, never>;
  /**
   * @deprecated State-derivable. Compare `s.threads.mainThreadId` against the
   * item's `s.threadListItem.id` via `useAuiState` instead. Kept for backward
   * compatibility.
   */
  switchedAway: Record<string, never>;
};

export type ThreadListItemEventType = keyof ThreadListItemEventPayload;

export type ThreadListItemEventCallback<E extends ThreadListItemEventType> = (
  payload: ThreadListItemEventPayload[E],
) => void;

import type { ThreadListItemRuntimeState } from "./bindings";
import type { ThreadListItemStatus } from "../interfaces/thread-list-runtime-core";

export type { ThreadListItemRuntimeState, ThreadListItemStatus };

export type ThreadListItemGenerateTitleOptions = {
  /** Marks a generation started by the automatic title trigger. */
  automatic?: boolean;
};

export type ThreadListItemRuntime = {
  readonly path: ThreadListItemRuntimePath;
  getState(): ThreadListItemRuntimeState;

  initialize(): Promise<{ remoteId: string; externalId: string | undefined }>;
  generateTitle(options?: ThreadListItemGenerateTitleOptions): Promise<void>;

  switchTo(options?: { unarchive?: boolean }): Promise<void>;
  rename(newTitle: string): Promise<void>;
  updateCustom(custom: Record<string, unknown> | undefined): Promise<void>;
  archive(): Promise<void>;
  unarchive(): Promise<void>;
  delete(): Promise<void>;

  detach(): void;

  subscribe(callback: () => void): Unsubscribe;

  unstable_on<E extends ThreadListItemEventType>(
    event: E,
    callback: ThreadListItemEventCallback<E>,
  ): Unsubscribe;

  __internal_getRuntime(): ThreadListItemRuntime;
};

export type ThreadListItemStateBinding = SubscribableWithState<
  ThreadListItemRuntimeState,
  ThreadListItemRuntimePath
>;

export class ThreadListItemRuntimeImpl implements ThreadListItemRuntime {
  public get path() {
    return this._core.path;
  }

  private _core: ThreadListItemStateBinding;
  private _threadListBinding: ThreadListRuntimeCoreBinding;

  constructor(
    _core: ThreadListItemStateBinding,
    _threadListBinding: ThreadListRuntimeCoreBinding,
  ) {
    this._core = _core;
    this._threadListBinding = _threadListBinding;
    this.__internal_bindMethods();
  }

  protected __internal_bindMethods() {
    this.switchTo = this.switchTo.bind(this);
    this.rename = this.rename.bind(this);
    this.updateCustom = this.updateCustom.bind(this);
    this.archive = this.archive.bind(this);
    this.unarchive = this.unarchive.bind(this);
    this.delete = this.delete.bind(this);
    this.initialize = this.initialize.bind(this);
    this.generateTitle = this.generateTitle.bind(this);
    this.subscribe = this.subscribe.bind(this);
    this.unstable_on = this.unstable_on.bind(this);
    this.getState = this.getState.bind(this);
    this.detach = this.detach.bind(this);
  }

  public getState(): ThreadListItemRuntimeState {
    return this._core.getState();
  }

  public switchTo(options?: { unarchive?: boolean }): Promise<void> {
    const state = this._core.getState();
    return this._threadListBinding.switchToThread(state.id, options);
  }

  public rename(newTitle: string): Promise<void> {
    const state = this._core.getState();

    return this._threadListBinding.rename(state.id, newTitle);
  }

  public updateCustom(
    custom: Record<string, unknown> | undefined,
  ): Promise<void> {
    const state = this._core.getState();
    if (!this._threadListBinding.updateCustom) {
      throw new Error(
        "Thread list runtime does not support updating custom metadata",
      );
    }

    return this._threadListBinding.updateCustom(state.id, custom);
  }

  public archive(): Promise<void> {
    const state = this._core.getState();

    return this._threadListBinding.archive(state.id);
  }

  public unarchive(): Promise<void> {
    const state = this._core.getState();

    return this._threadListBinding.unarchive(state.id);
  }

  public delete(): Promise<void> {
    const state = this._core.getState();

    return this._threadListBinding.delete(state.id);
  }

  public initialize(): Promise<{
    remoteId: string;
    externalId: string | undefined;
  }> {
    const state = this._core.getState();
    return this._threadListBinding.initialize(state.id);
  }

  public generateTitle(
    options?: ThreadListItemGenerateTitleOptions,
  ): Promise<void> {
    const state = this._core.getState();
    return this._threadListBinding.generateTitle(state.id, options);
  }

  public unstable_on<E extends ThreadListItemEventType>(
    event: E,
    callback: ThreadListItemEventCallback<E>,
  ) {
    let prevIsMain = this._core.getState().isMain;
    let prevThreadId = this._core.getState().id;
    return this.subscribe(() => {
      const currentState = this._core.getState();
      const newIsMain = currentState.isMain;
      const newThreadId = currentState.id;
      if (prevIsMain === newIsMain && prevThreadId === newThreadId) return;
      prevIsMain = newIsMain;
      prevThreadId = newThreadId;

      if (event === "switchedTo" && !newIsMain) return;
      if (event === "switchedAway" && newIsMain) return;
      notifyEventListeners(
        [callback as (payload?: unknown) => void],
        {},
        `Thread list item "${event}"`,
      );
    });
  }

  public subscribe(callback: () => void): Unsubscribe {
    return this._core.subscribe(callback);
  }

  public detach(): void {
    const state = this._core.getState();

    this._threadListBinding.detach(state.id);
  }

  public __internal_getRuntime(): ThreadListItemRuntime {
    return this;
  }
}
